import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gavel, FileWarning, FileText, Download, CheckCircle2, ChevronDown, ClipboardList } from "lucide-react";
import api from "../lib/api";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const meseLabel = (ym) => { const [y, m] = (ym || "").split("-"); return `${MESI[Number(m) - 1] || ""} ${y || ""}`; };
const longDate = (iso) => { try { const s = new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return iso; } };

export const useMiaDisciplina = (opId) => useQuery({
  queryKey: ["mia-disciplina", opId], enabled: !!opId, retry: false,
  queryFn: async () => (await api.get("/firma/mio-account/disciplina", { params: { op_id: opId } })).data,
});

const PvButton = ({ done, busy, onClick, testid }) => done
  ? <span className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1" data-testid={`${testid}-done`}><CheckCircle2 size={13} /> Presa visione</span>
  : <button type="button" onClick={onClick} disabled={busy} data-testid={testid}
      className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg px-2.5 py-1 transition-colors">{busy ? "…" : "Ho preso visione"}</button>;

export default function MiaDisciplina({ opId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useMiaDisciplina(opId);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const pdf = async (tipo, id) => {
    try {
      const res = await api.get(`/firma/mio-account/disciplina/${tipo}/${id}/pdf`, { params: { op_id: opId }, responseType: "blob" });
      const u = URL.createObjectURL(res.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Errore nel PDF"); }
  };
  const pv = async (tipo, id) => {
    setBusy(id);
    try { await api.post(`/firma/mio-account/disciplina/${tipo}/${id}/presa-visione`, { op_id: opId }); toast.success("Presa visione registrata"); qc.invalidateQueries({ queryKey: ["mia-disciplina"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(null);
  };
  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Caricamento…</p>;
  const segs = data.segnalazioni || [];
  const lettere = data.lettere || [];
  return (
    <div className="space-y-5" data-testid="mia-disciplina">
      <div className="flex items-center gap-2"><Gavel size={18} className="text-red-600" /><h3 className="font-head font-bold">Le mie segnalazioni e richiami</h3><span className="text-xs text-muted-foreground">· ultimi {data.mesi} mesi</span></div>
      <p className="text-xs text-muted-foreground -mt-3">Leggi ogni voce e conferma con "Ho preso visione". La conferma viene registrata e comunicata al responsabile.</p>

      <div>
        <div className="text-[11px] font-bold uppercase tracking-wide text-orange-700 mb-2 flex items-center gap-1"><FileWarning size={13} /> Richiami ({lettere.length})</div>
        {lettere.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mia-lettere-empty">Nessun richiamo.</p> : (
          <div className="space-y-2">{lettere.map((l) => (
            <div key={l.id} className={`rounded-lg border p-3 flex items-center justify-between gap-2 flex-wrap ${l.pv_confermata ? "bg-white" : "border-orange-300 bg-orange-50"}`} data-testid={`mia-lettera-${l.id}`}>
              <div><div className="text-sm font-semibold">Lettera di richiamo · {l.titolo || meseLabel(l.mese)}</div><div className="text-[11px] text-muted-foreground">{l.n_segnalazioni} segnalazioni (soglia {l.soglia})</div></div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => pdf("lettera", l.id)} className="text-[11px] font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`mia-lettera-pdf-${l.id}`}><Download size={13} /> PDF</button>
                <PvButton done={l.pv_confermata} busy={busy === l.id} onClick={() => pv("lettera", l.id)} testid={`mia-lettera-pv-${l.id}`} />
              </div>
            </div>
          ))}</div>
        )}
      </div>

      <div>
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><FileText size={13} /> Segnalazioni ({segs.length})</div>
        {segs.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="mia-segnalazioni-empty">Nessuna segnalazione.</p> : (
          <div className="space-y-2">{segs.map((s) => {
            const isOpen = open === s.id;
            const cons = (s.motivo || "").toLowerCase().startsWith("consegne non effettuate");
            return (
              <div key={s.id} className={`rounded-lg border p-3 ${s.pv_confermata ? "bg-white" : "border-red-300 bg-red-50/60"}`} data-testid={`mia-seg-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <button type="button" onClick={() => setOpen(isOpen ? null : s.id)} className="text-left min-w-0 flex-1" data-testid={`mia-seg-toggle-${s.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <ChevronDown size={13} className={`text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      <span className="text-sm font-semibold">{longDate(s.data)}</span>
                      {cons && <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-violet-100 text-violet-800 inline-flex items-center gap-1"><ClipboardList size={11} /> Consegne</span>}
                    </div>
                    <div className="text-xs font-semibold mt-0.5">{s.motivo}</div>
                  </button>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => pdf("segnalazione", s.id)} className="text-[11px] font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`mia-seg-pdf-${s.id}`}><Download size={13} /> PDF</button>
                    <PvButton done={s.pv_confermata} busy={busy === s.id} onClick={() => pv("segnalazione", s.id)} testid={`mia-seg-pv-${s.id}`} />
                  </div>
                </div>
                {isOpen && <div className="mt-2 pt-2 border-t text-xs text-muted-foreground whitespace-pre-wrap" data-testid={`mia-seg-dett-${s.id}`}>{s.spiegazione}</div>}
              </div>
            );
          })}</div>
        )}
      </div>
    </div>
  );
}
